import { useState } from "react";
import OfferingTab from "./OfferingTab";
import TimelineTab from "./TimelineTab";
import BasicDetailsTab from "./BasicDetailsTab";
import PerformanceTab from "./PerformanceTab";
import ListsTab from "./ListsTab";
import PartiesTab from "./PartiesTab";
import CapitalTab from "./CapitalTab";
import { TabProps } from "../types";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/IPO/components/ui/tabs";
import {
  Building2,
  TrendingUp,
  ListChecks,
  Users,
  IndianRupee,
} from "lucide-react";

// Extend TabProps to include specific props needed for sub-tabs
interface IPODetailsTabProps extends TabProps {
  bidStartDate: string;
  bidEndDate: string;
  allotmentDate: string;
  refundDate: string;
  listingDate: string;
  setBidStartDate: (value: string) => void;
  setBidEndDate: (value: string) => void;
  setAllotmentDate: (value: string) => void;
  setRefundDate: (value: string) => void;
  setListingDate: (value: string) => void;
  convertToFormFormat: (dateStr: string) => string;
  financials: any[];
  setFinancials: (data: any[]) => void;
  peers: any[];
  setPeers: (data: any[]) => void;
  objectives: any[];
  setObjectives: (data: any[]) => void;
  promoters: any[];
  setPromoters: (data: any[]) => void;
  financialAmountUnit?: string;
  onFinancialAmountUnitChange?: (unit: string) => void;
  objectsOfIssueAmountUnit?: string;
  onObjectsOfIssueAmountUnitChange?: (unit: string) => void;
}

const IPODetailsTab = ({
  formData,
  handleInputChange,
  handleSelectChange,
  validationErrors = {},
  readOnly = false,
  bidStartDate,
  bidEndDate,
  allotmentDate,
  refundDate,
  listingDate,
  setBidStartDate,
  setBidEndDate,
  setAllotmentDate,
  setRefundDate,
  setListingDate,
  convertToFormFormat,
  financials,
  setFinancials,
  peers,
  setPeers,
  objectives,
  setObjectives,
  promoters,
  setPromoters,
  financialAmountUnit,
  objectsOfIssueAmountUnit,
}: IPODetailsTabProps) => {
  const [activeTab, setActiveTab] = useState("offering");

  return (
    <Tabs
      defaultValue="offering"
      className="w-full space-y-6"
      value={activeTab}
      onValueChange={setActiveTab}
    >
      <div className="scrollbar-none overflow-x-auto border-b border-slate-200">
        <div className="flex w-full items-center justify-between">
          <TabsList className="h-12 w-fit justify-start gap-2 bg-transparent p-0">
            <TabsTrigger
              value="offering"
              className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              <IndianRupee className="h-4 w-4" />
              IPO Details
            </TabsTrigger>
            <TabsTrigger
              value="basic"
              className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              <Building2 className="h-4 w-4" />
              Company Info
            </TabsTrigger>
            <TabsTrigger
              value="performance"
              className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              <TrendingUp className="h-4 w-4" />
              Financials
            </TabsTrigger>
            <TabsTrigger
              value="capital"
              className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              <TrendingUp className="h-4 w-4" />
              Structure
            </TabsTrigger>
            <TabsTrigger
              value="lists"
              className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              <ListChecks className="h-4 w-4" />
              Peers & Lists
            </TabsTrigger>
            <TabsTrigger
              value="parties"
              className="group flex h-12 items-center gap-2 rounded-none border-b-2 border-transparent px-6 text-sm font-medium text-slate-500 data-[state=active]:border-primary data-[state=active]:text-primary"
            >
              <Users className="h-4 w-4" />
              Parties
            </TabsTrigger>
          </TabsList>
          <div className="mr-4 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 shadow-sm transition-all hover:border-slate-300">
            <input
              type="checkbox"
              id="isPublish"
              checked={formData?.status === "Open"}
              onChange={e =>
                handleSelectChange(
                  "status",
                  e.target.checked ? "Open" : "Upcoming"
                )
              }
              className="h-4 w-4 cursor-pointer appearance-none rounded border border-gray-300 bg-white bg-center bg-no-repeat align-middle transition-all checked:border-primary checked:bg-primary checked:bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20viewBox%3D%220%200%2016%2016%22%20fill%3D%22white%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cpath%20d%3D%22M12.207%204.793a1%201%200%20010%201.414l-5%205a1%201%200%2001-1.414%200l-2-2a1%201%200%20011.414-1.414L6.5%209.086l4.293-4.293a1%201%200%20011.414%200z%22%2F%3E%3C%2Fsvg%3E')] focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            />
            <label
              htmlFor="isPublish"
              className="cursor-pointer text-sm font-medium text-slate-700"
            >
              Publish
            </label>
          </div>
        </div>
      </div>

      <TabsContent value="basic" className="mt-0">
        <BasicDetailsTab
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          validationErrors={validationErrors}
          readOnly={readOnly}
        />
      </TabsContent>

      <TabsContent value="offering" className="mt-0 space-y-6">
        <OfferingTab
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          validationErrors={validationErrors}
          readOnly
        />
        <TimelineTab
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          bidStartDate={bidStartDate}
          bidEndDate={bidEndDate}
          allotmentDate={allotmentDate}
          refundDate={refundDate}
          listingDate={listingDate}
          setBidStartDate={setBidStartDate}
          setBidEndDate={setBidEndDate}
          setAllotmentDate={setAllotmentDate}
          setRefundDate={setRefundDate}
          setListingDate={setListingDate}
          convertToFormFormat={convertToFormFormat}
          validationErrors={validationErrors}
          readOnly
        />
      </TabsContent>

      <TabsContent value="performance" className="mt-0">
        <PerformanceTab
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          validationErrors={validationErrors}
          readOnly={readOnly}
        />
      </TabsContent>

      <TabsContent value="capital" className="mt-0">
        <CapitalTab
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          validationErrors={validationErrors}
          readOnly={readOnly}
        />
      </TabsContent>

      <TabsContent value="lists" className="mt-0">
        <ListsTab
          financials={financials}
          setFinancials={setFinancials}
          peers={peers}
          setPeers={setPeers}
          objectives={objectives}
          setObjectives={setObjectives}
          promoters={promoters}
          setPromoters={setPromoters}
          financialAmountUnit={financialAmountUnit}
          objectsOfIssueAmountUnit={objectsOfIssueAmountUnit}
          readOnly={readOnly}
        />
      </TabsContent>

      <TabsContent value="parties" className="mt-0">
        <PartiesTab
          formData={formData}
          handleInputChange={handleInputChange}
          handleSelectChange={handleSelectChange}
          validationErrors={validationErrors}
          readOnly={readOnly}
        />
      </TabsContent>
    </Tabs>
  );
};

export default IPODetailsTab;
